<template>
  <div class="debt-installments">
    <Loader 
      v-if="loaderEnabled" 
      v-bind:logo="loaderLogo"
    />
    
    <!-- MAIN CONTAINER -->
    <b-row>
      <b-col md="12" lg="12" sm="12">
        <KTCodePreview>
          <template v-slot:preview>
            <!-- FILTER COMPONENT -->
            <DebtInstallmentFilter
              v-model="filters"
              :installments="availableInstallments"
              :is-loading="onLoadingList"
              @apply-filters="onApplyFilters"
            />

            <!-- TABLE COMPONENT -->
            <DebtInstallmentTable
              :data="mainList.dataset"
              :is-loading="onLoadingList"
              :current-page="apiParams.page"
              :page-size="apiParams.pageSize"
              :totals="calculatedTotals"
            />

            <!-- PAGINATION COMPONENT -->
            <DebtInstallmentPagination
              :total="mainList.total"
              :total-pages="mainList.pages"
              :current-page="apiParams.page"
              :page-size="apiParams.pageSize"
              @page-change="onPageChange"
            />
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { getToastInstance } from '@/utils/toastHelper';
import KTCodePreview from '@/view/content/CodePreview.vue';
import Loader from '@/view/content/Loader.vue';

// Import refactored components
import DebtInstallmentFilter from './components/DebtInstallmentFilter.vue';
import DebtInstallmentTable from './components/DebtInstallmentTable.vue';
import DebtInstallmentPagination from './components/DebtInstallmentPagination.vue';

// Import service layer
import DebtInstallmentService from './services/DebtInstallmentService.js';

export default {
  name: 'DebtInstallmentList',
  components: {
    KTCodePreview,
    Loader,
    DebtInstallmentFilter,
    DebtInstallmentTable,
    DebtInstallmentPagination,
  },
  data() {
    return {
      title: 'Công nợ dịch vụ trả góp',
      
      // Main data
      mainList: {
        dataset: [],
        total: 0,
        pages: 1,
      },
      
      // Filter state
      filters: {
        fromDate: null,
        toDate: null,
        searchInstallment: '',
        installmentId: null,
      },
      
      // API parameters
      apiParams: {
        page: 1,
        pageSize: 10,
        fromDate: null,
        toDate: null,
        installmentId: null,
        type: null,
      },
      
      // UI state
      onLoadingList: false,
      availableInstallments: [],
    };
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    
    /**
     * Check if loader is enabled
     */
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    
    /**
     * Get loader logo path
     */
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
    
    /**
     * Calculate summary totals for the current dataset
     */
    calculatedTotals() {
      return DebtInstallmentService.calculateSummaryTotals(this.mainList.dataset);
    },
  },
  async created() {
    // Initialize component
    await this.initializeComponent();
  },
  methods: {
    ...getToastInstance(),
    
    /**
     * Initialize component with data loading
     */
    async initializeComponent() {
      try {
        this.loadBreadcrumb();
        await Promise.all([
          this.fetchInstallments(),
          this.fetchMainData(),
        ]);
      } catch (error) {
        console.error('Error initializing component:', error);
        this.makeToastFailure('Lỗi khởi tạo trang');
      }
    },
    
    /**
     * Load breadcrumb navigation
     */
    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kế toán', route: 'accountant' },
        { title: 'Công nợ' },
        { title: this.title },
      ]);
    },
    
    /**
     * Fetch installments for autocomplete
     */
    async fetchInstallments() {
      try {
        this.availableInstallments = await DebtInstallmentService.getInstallmentsForAutocomplete();
      } catch (error) {
        console.error('Error fetching installments:', error);
        this.makeToastFailure('Lỗi tải danh sách dịch vụ trả góp');
      }
    },
    
    /**
     * Fetch main debt installment data
     */
    async fetchMainData() {
      try {
        this.onLoadingList = true;
        
        // Get current page from route
        const page = this.$route.query.page || 1;
        this.apiParams.page = parseInt(page);
        this.apiParams.type = this.$route.params.mode;
        
        // Build API parameters
        const params = DebtInstallmentService.buildApiParameters(
          { ...this.filters, ...this.apiParams },
          this.$route.params
        );
        
        // Fetch data
        const response = await DebtInstallmentService.getDebtInstallments(params);
        this.mainList = response;
        
      } catch (error) {
        console.error('Error fetching main data:', error);
        const message = error.response?.data?.message || 'Lỗi tải dữ liệu';
        this.makeToastFailure(message);
      } finally {
        this.onLoadingList = false;
      }
    },
    
    /**
     * Handle filter application
     * @param {Object} filterValues - Applied filter values
     */
    async onApplyFilters(filterValues) {
      try {
        this.filters = { ...filterValues };
        this.apiParams.page = 1; // Reset to first page
        await this.fetchMainData();
      } catch (error) {
        console.error('Error applying filters:', error);
        this.makeToastFailure('Lỗi áp dụng bộ lọc');
      }
    },
    
    /**
     * Handle page change
     * @param {number} page - New page number
     */
    async onPageChange(page) {
      try {
        this.apiParams.page = page;
        await this.fetchMainData();
      } catch (error) {
        console.error('Error changing page:', error);
        this.makeToastFailure('Lỗi chuyển trang');
      }
    },
  },
  
  /**
   * Handle route updates
   */
  beforeRouteUpdate(to, from, next) {
    this.apiParams.type = to.params.mode;
    next();
  },
  
  /**
   * Watch for route parameter changes
   */
  watch: {
    '$route.params.mode': {
      handler(newMode) {
        if (newMode !== undefined) {
          this.apiParams.type = newMode;
          this.fetchMainData();
          this.loadBreadcrumb();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-installments {
  // Component-specific styles
  .icon:hover {
    background-color: #90c6fc;
  }
}
</style>
